import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import {
  ContestResult,
  ContestState
} from '@maxpreps/apiserviceclients/apiModels';
import { publicOrigin } from '@maxpreps/network/src/origins';
import SportName from '@maxpreps/shared/constants/sportName';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import THEME, { COLOR } from '@maxpreps/shared/styles/colors';
import ExpandableList, {
  IListItemProps
} from '@maxpreps/shared/ui/ExpandableList/ExpandableList';
import { IconType } from '@maxpreps/shared/ui/Icon/Icon';
import { iconBracket } from '@maxpreps/shared/ui/Icon/mp/Bracket';
import { iconHudl } from '@maxpreps/shared/ui/Icon/mp/Hudl';
import { IClientContest } from '../../../utils/schedule/serializeSchedule';
import useScheduleContext from '../ScheduleContext';
import {
  getAdminEnterScoresLink,
  getAdminHudlStatsLink
} from './../../../utils/navigation';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const getBracketLink = contest => {
  const { tournamentId, tournamentBracketId, sportSeasonId } = contest;

  if (!tournamentId || !tournamentBracketId) {
    return null;
  }
  if (tournamentId === EMPTY_GUID || tournamentBracketId === EMPTY_GUID) {
    return null;
  }

  return `${publicOrigin}/tournament/view.aspx?bracketid=${tournamentBracketId}&tournamentid=${tournamentId}&ssid=${sportSeasonId}`;
};

const getListItems = (contest, sportData) => {
  const {
    location,
    contestState,
    currentTeam,
    opponentTeam,
    isHudlConnected,
    hasHudlStatsPending,
    bracketGameIndex,
    bracketGamesInMatchup
  } = contest;

  const { hasHudlStatsImported } = currentTeam;

  const isGameOver =
    contestState === ContestState.Boxscore ||
    contestState === ContestState.ScoreNotReported ||
    // Treating deleted games as "completed" games so the stats icons are still displayed
    contestState === ContestState.Deleted;

  const listItems: IListItemProps[] = [];
  // Notes
  if (location) {
    listItems.push({
      key: 'notes',
      text: location,
      className: 'notes'
    });
  }
  // Bracket

  const bracketLink = getBracketLink(contest);

  if (bracketLink) {
    listItems.push({
      key: 'bracket',
      text: 'View Bracket',
      iconType: IconType.MaxPreps,
      icon: iconBracket,
      href: bracketLink
    });
  }

  // Hudl and Stats
  if (isHudlConnected || isGameOver) {
    const statsRowItems: IListItemProps[] = [];

    // Hudl
    if (isHudlConnected) {
      statsRowItems.push({
        key: 'hudl',
        text:
          hasHudlStatsImported && !hasHudlStatsPending
            ? 'Hudl Stats Imported'
            : 'Connected to Hudl',
        iconColor:
          hasHudlStatsPending || hasHudlStatsImported
            ? THEME.social.hudl
            : undefined,
        iconType: IconType.MaxPreps,
        icon: iconHudl,
        adminOnly: true
      });
    }

    if (isGameOver) {
      // My Team Stats
      statsRowItems.push({
        key: 'teamStats',
        text: 'My Team Stats',
        icon: currentTeam.hasStats ? faCheck : faTimes,
        adminOnly: true
      });

      // Opponent Team Stats
      statsRowItems.push({
        key: 'opponentTeamStats',
        text: 'Opponent Stats',
        icon: opponentTeam.hasStats ? faCheck : faTimes,
        adminOnly: true
      });
    }

    // Keep Hudl and stats on the same row
    listItems.push({
      key: 'statsRow',
      cols: statsRowItems
    });
  }

  // Baseball and Softball Series Info.
  if (
    bracketGamesInMatchup &&
    bracketGamesInMatchup > 1 &&
    (sportData.name === SportName.Baseball ||
      sportData.name === SportName.Softball)
  ) {
    listItems.push({
      key: 'series',
      text: `${bracketGameIndex + 1}/${bracketGamesInMatchup} of Series`,
      iconType: IconType.FontAwesome,
      icon: faExclamationCircle
    });
  }

  return listItems;
};

const getOuterListItems = (contest, teamContext) => {
  const { hasHudlStatsPending, currentTeam, contestState } = contest;
  const { teamId, sportSeasonId, allSeasonId } = teamContext.data;
  const { hasStats, calculatedTeamContestResult } = currentTeam || {};

  const isGameOver =
    contestState === ContestState.Boxscore ||
    contestState === ContestState.ScoreNotReported ||
    // Treating deleted games as "completed" games so the stats icons are still displayed
    contestState === ContestState.Deleted;

  const isForfeit =
    calculatedTeamContestResult === ContestResult.WinByForfeit ||
    calculatedTeamContestResult === ContestResult.LossByForfeit ||
    calculatedTeamContestResult === ContestResult.DoubleForfeit;

  const enterScoresLink = getAdminEnterScoresLink(
    teamId,
    allSeasonId,
    sportSeasonId,
    contest.contestId
  );

  const hudlStatsLink = getAdminHudlStatsLink(
    teamId,
    allSeasonId,
    sportSeasonId,
    contest.contestId
  );

  const outerListItems: IListItemProps[] = [];
  if (isGameOver && !isForfeit && !hasStats && hasHudlStatsPending) {
    outerListItems.push({
      key: 'hudlStandingsPending',
      href: hudlStatsLink,
      icon: faExclamationCircle,
      iconColor: COLOR.red50,
      text: 'Hudl Stats Pending',
      adminOnly: true
    });
  }

  if (isGameOver && !isForfeit && !hasStats) {
    outerListItems.push({
      key: 'missingStats',
      href: enterScoresLink,
      icon: faExclamationCircle,
      iconColor: COLOR.red50,
      text: 'Missing Stats',
      adminOnly: true
    });
  }

  return outerListItems;
};

interface IContestDetailsListProps {
  contest: IClientContest;
}

const ContestDetailsList: React.FC<IContestDetailsListProps> = ({
  contest
}) => {
  const teamContext = useTeamContext();
  const scheduleContext = useScheduleContext();

  const listItems = getListItems(contest, teamContext.sportData);
  const outerListItems = getOuterListItems(contest, teamContext);

  if (!listItems?.length && !outerListItems?.length) return null;

  const isExpanded = scheduleContext?.expandedContestIds.includes(
    contest.contestId
  );

  return (
    <ExpandableList
      listItems={listItems}
      outerListItems={outerListItems}
      isExpanded={isExpanded}
    />
  );
};

export default ContestDetailsList;
